import { useCallback, useRef, useState } from 'react'

const DEFAULT_RESET_DELAY = 1500

interface UseCopyToClipboardOptions {
  readonly resetDelay?: number
  readonly onSuccess?: () => void
  readonly onError?: (error: Error) => void
}

interface UseCopyToClipboardResult {
  readonly copied: boolean
  readonly copy: (text: string) => Promise<boolean>
  readonly reset: () => void
}

export function useCopyToClipboard(
  options: UseCopyToClipboardOptions = {},
): UseCopyToClipboardResult {
  const { resetDelay = DEFAULT_RESET_DELAY, onSuccess, onError } = options
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setCopied(false)
  }, [])

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        onSuccess?.()

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          setCopied(false)
          timeoutRef.current = null
        }, resetDelay)

        return true
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Failed to copy to clipboard')
        onError?.(err)
        return false
      }
    },
    [resetDelay, onSuccess, onError],
  )

  return { copied, copy, reset }
}
